import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";

interface ITransactionLogUserDetails {
  _id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ITransactionLog {
  _id: string;
  user: string;
  updatedBy: string;
  beforeBalance: number;
  afterBalance: number;
  createdAt: string;
  userDetails?: ITransactionLogUserDetails;
  updatedByDetails?: ITransactionLogUserDetails;
}

interface IStore {
  logs: ITransactionLog[];
  hasNextPage: boolean;
  userLogs: ITransactionLog[];
  hasNextUserLogsPage: boolean;
  searchText: string;
  getTransactionLogs: (page?: number, limit?: number, search?: string) => void;
  getTransactionLogsByUser: (userId: string, page?: number, limit?: number) => void;
  setSearchText: (text: string) => void;
  clearSearchText: () => void;
}

export const useTransactionLogs = create<IStore>()((set, get) => ({
  logs: [],
  hasNextPage: false,
  userLogs: [],
  hasNextUserLogsPage: false,
  searchText: "",
  getTransactionLogs: async (page = 1, limit = 20, search = "") => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("/transaction-logs", {
        params: {
          page,
          limit,
          search,
        },
      });
      if (response?.data) {
        const data = response.data as ITransactionLog[];
        set((state) => ({
          logs: page === 1 ? data : [...state.logs, ...data],
          hasNextPage: data.length === limit,
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching transaction logs", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getTransactionLogsByUser: async (userId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("/transaction-logs", {
        params: {
          page,
          limit,
          search: userId,
        },
      });
      if (response?.data) {
        const data = response.data as ITransactionLog[];
        set((state) => ({
          userLogs: page === 1 ? data : [...state.userLogs, ...data],
          hasNextUserLogsPage: data.length === limit,
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching transaction logs by user", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  setSearchText: (text: string) => set({ searchText: text }),
  clearSearchText: () => set({ searchText: "" }),
}));

